import { useState } from "react";
import { Phone, Mail, MapPin, ArrowRight, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { apiClient, COMPANY } from "@/lib/api";
import useReveal from "@/hooks/useReveal";

export default function Contact() {
  const ref = useReveal();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/contact", form);
      toast.success("Thanks! We'll reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Could not send. Please try WhatsApp or email.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "mt-2 w-full bg-[#F8F9FA] border border-gray-200 focus:border-[#B01116] focus:ring-1 focus:ring-[#B01116] outline-none px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 rounded-lg transition-colors";

  return (
    <section
      id="contact"
      ref={ref}
      data-testid="contact-section"
      className="py-16 sm:py-24 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 fade-up">
          <span className="gmb-badge-yellow">Free Growth Audit</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#B01116]">
            Let&apos;s Build Your Growth System.
          </h2>
          <p className="mt-5 text-gray-600 text-base leading-relaxed">
            Drop your details and we&apos;ll audit your current funnel within 24 hours —
            no obligation, no fluff.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`tel:${COMPANY.phone}`} data-testid="contact-phone" className="flex items-start gap-4 group">
              <div className="h-11 w-11 flex-none flex items-center justify-center bg-[#FDE8E8] text-[#B01116] rounded-full group-hover:bg-[#B01116] group-hover:text-white transition-colors">
                <Phone className="h-4 w-4" />
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider font-accent font-bold">Call</div>
                <div className="text-gray-900 font-semibold">{COMPANY.phoneDisplay}</div>
              </div>
            </a>
            <a
              href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(
                "Hi Gold Mind Business, I'd like to discuss growth for my business."
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="flex items-start gap-4 group"
            >
              <div
                className="h-11 w-11 flex-none flex items-center justify-center rounded-full transition-colors"
                style={{ backgroundColor: "rgba(37, 211, 102, 0.12)", color: "#25D366" }}
              >
                <MessageCircle className="h-4 w-4" />
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider font-accent font-bold">WhatsApp</div>
                <div className="text-gray-900 font-semibold">Chat with us instantly</div>
              </div>
            </a>
            <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="flex items-start gap-4 group">
              <div className="h-11 w-11 flex-none flex items-center justify-center bg-[#FDE8E8] text-[#B01116] rounded-full group-hover:bg-[#B01116] group-hover:text-white transition-colors">
                <Mail className="h-4 w-4" />
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider font-accent font-bold">Email</div>
                <div className="text-gray-900 font-semibold">{COMPANY.email}</div>
              </div>
            </a>
            <div className="flex items-start gap-4" data-testid="contact-address">
              <div className="h-11 w-11 flex-none flex items-center justify-center bg-[#FDE8E8] text-[#B01116] rounded-full">
                <MapPin className="h-4 w-4" />
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider font-accent font-bold">Studio</div>
                <div className="text-gray-900 font-semibold leading-relaxed">{COMPANY.address}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 fade-up" style={{ transitionDelay: "100ms" }}>
          <form onSubmit={submit} data-testid="contact-form" className="gmb-card p-8 md:p-10 grid gap-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider font-accent font-bold">Name</label>
                <input
                  data-testid="contact-input-name"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your full name"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider font-accent font-bold">Email</label>
                <input
                  data-testid="contact-input-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@company.com"
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider font-accent font-bold">Phone</label>
              <input
                data-testid="contact-input-phone"
                required
                value={form.phone}
                onChange={update("phone")}
                placeholder="+91 ..."
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider font-accent font-bold">
                Tell us about your business
              </label>
              <textarea
                data-testid="contact-input-message"
                required
                value={form.message}
                onChange={update("message")}
                rows={5}
                placeholder="What do you sell? Current monthly revenue? Biggest growth bottleneck?"
                className={`${inputCls} resize-none`}
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <p className="text-xs text-gray-500 max-w-xs">
                We&apos;ll respond within 24 hours with a clear audit and next steps.
              </p>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={submitting}
                className="btn-gold gap-2 disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Enquiry <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
